import React from 'react';
import { X } from 'lucide-react';

/**
 * SliderRow — label · value (or –) · optional clear, over a range track.
 *
 * The unfilled track is painted from `--border-default` rather than left to the
 * browser: with only `accent-color` set, the track colour comes from the OS
 * scheme instead of ours, so a dark OS running the light theme drew a near-black
 * bar on white. `.trodo-range` (index.css) owns the track and thumb; the fill
 * gradient is set here because only this row knows where the value sits between
 * min and max.
 *
 * `value` of '' / null / undefined means UNSET — the row shows "–" and the
 * caller omits the param entirely rather than sending a default the user never
 * chose.
 */
export default function SliderRow({
  label, value, min, max, step, disabled, onChange, onClear, display,
}) {
  const set = value != null && value !== '';
  const pct = Math.min(100, Math.max(0, (((set ? Number(value) : min) - min) / (max - min)) * 100));
  const track = `linear-gradient(to right, var(--trodo-primary) 0%, var(--trodo-primary) ${pct}%, var(--border-default) ${pct}%, var(--border-default) 100%)`;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-medium text-[color:var(--text-primary)]">{label}</span>
        <div className="flex items-center gap-1.5">
          <span className="min-w-[28px] text-right font-mono text-[12px] text-[color:var(--text-secondary)]">
            {set ? (display ?? value) : '–'}
          </span>
          {set && !disabled && onClear && (
            <button
              type="button"
              onClick={onClear}
              title="Clear"
              className="grid h-4 w-4 place-items-center rounded border border-[color:var(--border-default)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
            >
              <X size={10} />
            </button>
          )}
        </div>
      </div>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        value={set ? Number(value) : min}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ background: track }}
        className="trodo-range w-full cursor-pointer disabled:opacity-40"
      />
    </div>
  );
}
